import { useEffect, useState } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { Loader2, AlertCircle } from 'lucide-react'
import { errorText } from '@papan/api-client'
import { useFileIO } from '@papan/files/client/file-io'
import { useRealtime, useOnlineRecovery } from '@papan/realtime/client'

interface PdfViewerProps {
  fileId: string
  fileName?: string
  versionId?: string
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  viewSyncTrigger?: number
}

export function PdfViewer({ fileId, versionId }: PdfViewerProps) {
  const [blob, setBlob] = useState<Blob | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  // #1316 review round 2: a load whose answer a session end dropped is asked again through `useDroppedLoadReask` —
  // in the share view it waits until the moved share session lands, never asking as the ended one
  const { reask, begin } = useDroppedLoadReask()
  const { onFileEvent } = useRealtime()
  const recovery = useOnlineRecovery()
  const fileIO = useFileIO()

  // Realtime updates: when the underlying file content changes, re-fetch.
  // versionId-pinned views never refresh (they're explicitly historical).
  useEffect(() => {
    if (versionId) return
    return onFileEvent((event) => {
      if (event.fileId === fileId && (event.type === 'file:updated' || event.type === 'file:restored')) {
        setRefreshCounter(c => c + 1)
      }
    })
  }, [fileId, versionId, onFileEvent])

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    let live = true
    setIsLoading(true)
    setError(null)
    fileIO.loadContent(fileId, versionId)
      .then(b => { if (live) { setBlob(b); setIsLoading(false) } })
      .catch(err => {
        if (!live) return
        const text = errorText(err, 'unknown error')
        // #1316: a dropped answer (its session ended) is asked again as the session now here — never an error
        if (text === null) { asked.dropped(); return }
        setError(text)
        setIsLoading(false)
      })
    return () => { live = false }
  }, [fileId, versionId, refreshCounter, recovery, fileIO, reask, begin])

  // Convert the loaded blob to an object URL inside an effect so cleanup
  // (URL.revokeObjectURL) is tied to the React lifecycle. Doing this in
  // useMemo previously caused the iframe to display a chrome-error
  // ("may have been moved") when the memo recomputed and revoked the URL
  // the iframe was still using.
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return
    }
    const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
    const objectUrl = URL.createObjectURL(pdfBlob)
    setUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [blob])

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm">Failed to load PDF: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-hidden themed-bg-primary">
      {url && (
        <iframe
          src={url}
          title="PDF Preview"
          className="w-full h-full border-0"
        />
      )}
    </div>
  )
}
